import React, { useMemo } from "react";
import { Alert, Box, Button, Container, Typography } from "@mui/material";
import { Form, useParams } from "react-router-dom";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { apiClientHooks } from "../bootstrapping/InitApiClient";
import { Helmet } from "react-helmet-async";
import { LocalFormSchema, zLocalFormSchema } from "../logic/applicationUpdateSchema";
import { getOldestDate, yearsSinceOldestDate } from "../utils/dates";
import AddressBlock from "../components/AddressBlock";
import { findGapsInDateRanges, previousAddressDateOrder, sortPreviousAddresses } from "../utils/addresses";
import { isSomething } from "../utils/utils";
import { TestingInfoBlock, TestingInfoBlockFormErrors } from "../components/TestingInfo";
import { ensureUppercase, trimTextFields } from "../utils/forms";
import { applicationRefinement } from "../logic/dbsRules";
import { useRedirectToGDPRConsentIfRequired } from "./ApplicationGDPRConsent";
import FormButtons from "../components/FormButtons";
import TestingData from "../components/TestingData";
import { useActionPath } from "./ApplicationUpdate";
import TestApplicationAlert from "../components/TestApplicationAlert";

export const useStartDatesOfAllAddresses = (currentAddress: LocalFormSchema['CurrentAddress'], previousAddress: LocalFormSchema['PreviousAddress']) => {
  const addressDates = [
    currentAddress?.ResidentFromGyearMonth,
    ...(previousAddress?.flatMap(each => [
      each?.ResidentDates?.ResidentFromGyearMonth,
    ]) ?? []),
  ]

  const yearsSinceOldestAddress = yearsSinceOldestDate(addressDates, 'yyyy-MM')
  const needsMoreAddresses = yearsSinceOldestAddress != null && yearsSinceOldestAddress < 5
  const gapsInAddresses = findGapsInDateRanges([
    // Include the current address if it's not null
    currentAddress?.ResidentFromGyearMonth == null
      ? undefined
      : {
        from: currentAddress.ResidentFromGyearMonth,
        to: new Date().toISOString().slice(0, 7), // current date in YYYY-MM format
      },
    // Include the previous addresses, if there are any
    ...(previousAddress ?? []).map(each => ({
      from: each?.ResidentDates?.ResidentFromGyearMonth,
      to: each?.ResidentDates?.ResidentToGyearMonth,
    }))
  ].filter(isSomething)
    // DBS-352: an address whose dates are not entered yet has no range to check
    .filter((each): each is { from: string, to: string } => Boolean(each.from) && Boolean(each.to)))

  return {
    addressDates,
    yearsSinceOldestAddress,
    needsMoreAddresses,
    gapsInAddresses,
  }
}

export const Element: React.FC = () => {
  const { application_id } = useParams<{ application_id?: string }>()
  if (!application_id) throw new Error('application_id is required')

  const getEBulkApplicationByID = apiClientHooks.useGetEBulkApplicationByID({ params: { id: application_id } })
  const readonly = getEBulkApplicationByID.data?.status !== 'draft'

  useRedirectToGDPRConsentIfRequired(application_id)
  const actionPath = useActionPath('documents')

  // Build the Form
  const form = useForm<LocalFormSchema>({
    resolver: zodResolver(
      zLocalFormSchema
        .superRefine(applicationRefinement('addresses'))
    ),
    mode: 'onBlur',
    defaultValues: {
      ...getEBulkApplicationByID.data?.data as any, // NOTE: This is a hack, as I don't understand the typescript error!
      CurrentAddress: {
        ...getEBulkApplicationByID.data?.data.CurrentAddress ?? {
          Address: {
            AddressLine1: '',
            AddressLine2: '',
            AddressTown: '',
            AddressCounty: '',
            Postcode: '',
            CountryCode: '',
          },
        },
        ResidentFromGyearMonth: getEBulkApplicationByID.data?.data.CurrentAddress?.ResidentFromGyearMonth ?? null,
      },
    },
  })

  // Find all start dates of all addresses
  const currentAddress = form.watch('CurrentAddress')
  const previousAddress = form.watch('PreviousAddress')
  const {
    addressDates,
    yearsSinceOldestAddress,
    needsMoreAddresses,
    gapsInAddresses,
  } = useStartDatesOfAllAddresses(currentAddress, previousAddress)

  // Build the list of previous addresses
  const previousAddresses = useFieldArray({
    control: form.control,
    name: 'PreviousAddress',
  })

  // Evaluate the previous address order
  // DBS-352: dated addresses only; one just added has no dates yet
  const getDateOrderAsString = previousAddressDateOrder

  const currentDateOrder = getDateOrderAsString(previousAddresses.fields)

  const { arePreviousAddressesInOrder, reorderPreviousAddresses } = useMemo(() => {
    const sortedPreviousAddresses = sortPreviousAddresses(previousAddresses.fields)

    if (previousAddresses.fields == null) return {
      arePreviousAddressesInOrder: true,
      reorderPreviousAddresses: () => { },
    }

    const sortedDateOrder = getDateOrderAsString(sortedPreviousAddresses)

    return {
      arePreviousAddressesInOrder: currentDateOrder === sortedDateOrder,
      reorderPreviousAddresses: () => {
        console.log('[order-order] !!! Replacing previous addresses with sorted ones', sortedPreviousAddresses)
        previousAddresses.replace(sortedPreviousAddresses)
      },
    }
  }, [currentDateOrder, previousAddresses])

  // Add an empty previous address, which will immediately have errors
  const addPreviousAddress = (toDate?: string, fromDate?: string, position?: number) => {
    const oldestDate = getOldestDate(addressDates)

    const newPreviousAddress = {
      Address: {
        AddressLine1: '',
        AddressLine2: '',
        AddressTown: '',
        AddressCounty: '',
        Postcode: '',
        CountryCode: '' as any,
      },
      ResidentDates: {
        ResidentToGyearMonth: toDate ?? oldestDate,
        ResidentFromGyearMonth: fromDate ?? oldestDate,
      },
    } as NonNullable<LocalFormSchema['PreviousAddress']>[number]

    if (position !== undefined) {
      previousAddresses.insert(position, newPreviousAddress)
    } else {
      previousAddresses.append(newPreviousAddress)
    }
  }

  return <>
    <Helmet>
      <title>{readonly ? 'View' : 'Update'} Addresses</title>
    </Helmet>
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5" marginBottom={3}>
          {readonly ? 'View' : 'Update'} DBS Application
        </Typography>
        <TestApplicationAlert dbsApp={getEBulkApplicationByID.data} sx={{ marginBottom: 3 }} />
        <Typography component="h2" variant="h5" marginBottom={3}>
          Addresses
        </Typography>
        <Typography variant="body1" marginBottom={1}>
          You need to provide your addresses for the last 5 years.
        </Typography>

        <Form action={actionPath.url} method="post" onBlur={() => {
          trimTextFields(form)
          ensureUppercase(form, 'CurrentAddress.Address.AddressLine1')
          ensureUppercase(form, 'CurrentAddress.Address.AddressLine2')
          ensureUppercase(form, 'CurrentAddress.Address.AddressTown')
          ensureUppercase(form, 'CurrentAddress.Address.AddressCounty')
          ensureUppercase(form, 'CurrentAddress.Address.Postcode')
          previousAddresses.fields.forEach((_, index) => {
            ensureUppercase(form, `PreviousAddress.${index}.Address.AddressLine1`)
            ensureUppercase(form, `PreviousAddress.${index}.Address.AddressLine2`)
            ensureUppercase(form, `PreviousAddress.${index}.Address.AddressTown`)
            ensureUppercase(form, `PreviousAddress.${index}.Address.AddressCounty`)
            ensureUppercase(form, `PreviousAddress.${index}.Address.Postcode`)
          })
        }}>
          {!readonly && (
            <TestingInfoBlock>
              <TestingData form={form} page="addresses" />
            </TestingInfoBlock>
          )}
          <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
            Current Address
          </Typography>
          <Alert severity="info" sx={{ marginBottom: 1 }}>
            If you do not have a permanent UK address, please ask your employer for a “Care Of” address to use here.
          </Alert>
          <AddressBlock
            form={form}
            allowNoFixedAbode={false}
            addressLine1="CurrentAddress.Address.AddressLine1"
            addressLine2="CurrentAddress.Address.AddressLine2"
            addressTown="CurrentAddress.Address.AddressTown"
            addressCounty="CurrentAddress.Address.AddressCounty"
            postcode="CurrentAddress.Address.Postcode"
            countryCode="CurrentAddress.Address.CountryCode"
            residentFromGyearMonth="CurrentAddress.ResidentFromGyearMonth"
            disabled={readonly}
          />

          {previousAddresses.fields.map((eachPreviousAddress, index) => {
            return (
              <React.Fragment key={eachPreviousAddress.id}>
                <Typography component="h3" variant="h6" marginTop={3} marginBottom={1}>
                  Previous Address #{index + 1}
                  {!readonly && <Button sx={{ marginLeft: 1 }} onClick={() => previousAddresses.remove(index)}>Remove</Button>}
                </Typography>
                <AddressBlock
                  form={form}
                  allowNoFixedAbode={true}
                  addressLine1={`PreviousAddress.${index}.Address.AddressLine1`}
                  addressLine2={`PreviousAddress.${index}.Address.AddressLine2`}
                  addressTown={`PreviousAddress.${index}.Address.AddressTown`}
                  addressCounty={`PreviousAddress.${index}.Address.AddressCounty`}
                  postcode={`PreviousAddress.${index}.Address.Postcode`}
                  countryCode={`PreviousAddress.${index}.Address.CountryCode`}
                  residentToGyearMonth={`PreviousAddress.${index}.ResidentDates.ResidentToGyearMonth`}
                  residentFromGyearMonth={`PreviousAddress.${index}.ResidentDates.ResidentFromGyearMonth`}
                  disabled={readonly}
                />
              </React.Fragment>
            )
          })}

          {!arePreviousAddressesInOrder
            ? (
              <Alert severity="warning" sx={{ marginTop: 3 }}>
                The previous addresses are not in date order.
                {!readonly && <Button onClick={reorderPreviousAddresses}>Reorder Chronologically</Button>}
              </Alert>
            )
            : undefined}

          {gapsInAddresses.length > 0 && gapsInAddresses.map(gap =>
            <Alert severity="warning" sx={{ marginTop: 3 }}>
              There is a gap between {gap.from} and {gap.to}.
              {!readonly && <Button onClick={() => addPreviousAddress(gap.to, gap.from)}>Add address</Button>}
            </Alert>
          )}

          {needsMoreAddresses
            ? (
              <Alert severity="error" sx={{ marginTop: 3 }}>
                You have provided {yearsSinceOldestAddress} years of addresses, but we need 5 years.
                {!readonly && <Button onClick={() => addPreviousAddress()}>Add another address</Button>}
              </Alert>
            )
            : (
              <Alert severity="success" sx={{ marginTop: 3 }}>
                You have provided {yearsSinceOldestAddress} years of addresses.
                {!readonly && <>
                  You can still add more addresses if you would like.
                  <Button onClick={() => addPreviousAddress()}>Add another address</Button>
                </>}
              </Alert>
            )}

          <TestingInfoBlockFormErrors form={form} />

          <FormButtons actionPath={actionPath} form={form} readonly={readonly} />
        </Form>
      </Box>
    </Container>
  </>
}

export default Element
